import React from 'react';
import {
  Autocomplete,
  AutocompleteRenderInputParams,
  TextField,
  MenuItem,
  ListSubheader,
  useTheme,
  ClickAwayListener,
} from '@mui/material';
import { deepmerge } from '@mui/utils';
import { Box, BoxProps } from '../Box';
import { IconButton } from '../IconButton';
import { Icon } from '../Icon';
import { Flex } from '../Flex';
import { AbstractButton } from '../AbstractButton';
import { Spinner } from '../Spinner';
import { typographyTokens } from '../../theme/typography-tokens';

export interface SearchableComboboxProps<Item> {
  value: Item | null;
  onChange: (value: Item | null) => void;
  onOpen: () => void;
  onClose: () => void;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  open: boolean;
  items: Item[];
  itemToString: (item: Item) => string;
  itemToGroup?: (item: Item) => string;
  disableItem: (item: Item) => boolean;
  renderMenuItem?: (item: Item) => React.ReactNode;
  loading: boolean;
  disabled: boolean;
  invalid?: boolean;
  required?: boolean;
  label: string;
  hideLabel?: boolean;
  placeholder?: string;
  emptyText?: string;
  name?: string;
  maxResults?: number;
  maxHeight: number;
  maxWidth: number;
  showClearSelectionControl: boolean;
  disablePortal?: boolean;
  inputEndContent?: React.ReactNode;
  combinedSx: any;
  otherProps: any;
}

function SearchableComboboxComponent<Item>(
  {
    value,
    onChange,
    onOpen,
    onClose,
    onBlur,
    open,
    items,
    itemToString,
    itemToGroup,
    disableItem,
    renderMenuItem,
    loading,
    disabled,
    invalid,
    required,
    label,
    hideLabel,
    placeholder,
    emptyText,
    name,
    maxResults,
    maxHeight,
    maxWidth,
    showClearSelectionControl,
    disablePortal,
    inputEndContent,
    combinedSx,
    otherProps,
  }: SearchableComboboxProps<Item>,
  ref: React.Ref<HTMLDivElement>,
) {
  const theme = useTheme();
  const tokens = theme.palette?.tokens;

  return (
    <ClickAwayListener
      mouseEvent="onMouseDown"
      onClickAway={() => {
        if (open) {
          onClose();
        }
      }}
    >
      <Box ref={ref} position="relative" sx={combinedSx}>
        <Autocomplete<Item, false, false, false>
          value={value}
          onChange={(event, newValue) => onChange(newValue)}
          onOpen={onOpen}
          onClose={onClose}
          open={open}
          options={items}
          getOptionLabel={itemToString}
          isOptionEqualToValue={(option, val) => itemToString(option) === itemToString(val)}
          getOptionDisabled={disableItem}
          {...(itemToGroup && { groupBy: itemToGroup })}
          loading={loading}
          disabled={disabled}
          disableClearable
          popupIcon={null}
          disablePortal={disablePortal}
          filterOptions={(options, state) => {
            let result = options;

            if (state.inputValue) {
              result = options.filter(item => {
                const itemString = itemToGroup
                  ? `${itemToGroup(item)}${itemToString(item)}`
                  : itemToString(item);
                return itemString.toLowerCase().includes(state.inputValue.toLowerCase());
              });
            }

            if (itemToGroup) {
              result = [...result].sort((a, b) => {
                const groupA = itemToGroup(a) ?? '';
                const groupB = itemToGroup(b) ?? '';
                return groupA.localeCompare(groupB);
              });
            }

            if (maxResults) {
              result = result.slice(0, maxResults);
            }

            return result;
          }}
          renderInput={(params: AutocompleteRenderInputParams) => (
            <TextField
              {...params}
              label={!hideLabel ? label : undefined}
              placeholder={placeholder || emptyText}
              name={name}
              error={invalid}
              required={required}
              onBlur={onBlur}
              slotProps={{
                inputLabel: {
                  ...params.InputLabelProps,
                  shrink: value != null || !!emptyText || open,
                },
                input: {
                  ...params.InputProps,
                  endAdornment: null,
                },
              }}
              inputProps={{
                ...params.inputProps,
                'aria-label': hideLabel ? label : undefined,
              }}
              sx={deepmerge(
                {
                  '& .MuiAutocomplete-inputRoot .MuiAutocomplete-input': {
                    padding: '0 !important',
                  },
                },
                otherProps.inputProps?.sx || {},
              )}
            />
          )}
          renderOption={(props, option) => {
            const { key, ...optionProps } = props;
            const itemString = itemToString(option);
            return (
              <MenuItem {...optionProps} key={key} component="li">
                {renderMenuItem ? renderMenuItem(option) : itemString}
              </MenuItem>
            );
          }}
          renderGroup={params => (
            <li key={params.key}>
              <ListSubheader component="div" sx={{ fontWeight: 600 }}>
                {params.group}
              </ListSubheader>
              <ul style={{ padding: 0 }}>{params.children}</ul>
            </li>
          )}
          slotProps={{
            paper: {
              sx: {
                maxHeight,
                maxWidth: `${maxWidth}px`,
                width: 'auto',
                mt: '8px',
                transformOrigin: 'top',
                '&.MuiAutocomplete-paper': {
                  animation: 'growIn 200ms cubic-bezier(0.4, 0, 0.2, 1)',
                },
                '@keyframes growIn': {
                  '0%': {
                    opacity: 0,
                    transform: 'scale(0.75)',
                  },
                  '100%': {
                    opacity: 1,
                    transform: 'scale(1)',
                  },
                },
              },
            },
            popper: {
              modifiers: [
                {
                  name: 'offset',
                  options: {
                    offset: [0, 0],
                  },
                },
              ],
            },
            listbox: {
              component: React.forwardRef<HTMLUListElement, React.HTMLAttributes<HTMLUListElement>>(
                function ListboxComponent(props, listboxRef) {
                  return (
                    <Box as="ul" {...(props as BoxProps<'ul'>)} ref={listboxRef}>
                      {showClearSelectionControl && value !== null && tokens && (
                        <Box
                          component="li"
                          sx={{
                            position: 'sticky',
                            top: 0,
                            zIndex: 1,
                            listStyle: 'none',
                            backgroundColor: tokens?.background.neutral.mid.base,
                          }}
                        >
                          <AbstractButton
                            onClick={e => {
                              e.stopPropagation();
                              onChange(null);
                              onClose();
                            }}
                            sx={{
                              width: '100%',
                              fontSize: typographyTokens.fontSizes.xs,
                              fontWeight: typographyTokens.fontWeights.medium,
                              justifyContent: 'start',
                              color: tokens.content.text.accent[3].base,
                              '&:hover': { textDecoration: 'underline' },
                              py: '6px',
                              px: '12px',
                            }}
                          >
                            <Flex as="span" align="center" justify="start" spacing={1}>
                              <Icon type="close-circle" size="small" color="inherit" />
                              <Box as="span">Clear</Box>
                            </Flex>
                          </AbstractButton>
                        </Box>
                      )}
                      {props.children}
                    </Box>
                  );
                },
              ),
              sx: {
                maxHeight,
                overflow: 'auto',
                borderRadius: '6px',
                padding: '6px',
              },
            },
          }}
          {...otherProps}
        />

        {/* Custom icon button positioned absolutely - matching Select */}
        <Box
          sx={{
            position: 'absolute',
            top: hideLabel ? '50%' : 'calc(50%)',
            right: '8px',
            transform: 'translateY(-50%)',
            display: 'flex',
            alignItems: 'center',
            pointerEvents: 'none',
            opacity: disabled ? 0.3 : 1,
          }}
        >
          {loading ? (
            <Spinner size="small" sx={{ mr: 2 }} />
          ) : (
            <Flex align="center" gap={1}>
              {inputEndContent && <Box>{inputEndContent}</Box>}
              <IconButton
                tabIndex={-1}
                icon={open ? 'caret-up' : 'caret-down'}
                size="medium"
                aria-label="Toggle Menu"
                disabled={disabled}
                variant="ghost"
                sx={{ pointerEvents: 'auto' }}
                onMouseDown={e => {
                  // Prevent the input from getting focus which would reopen the menu
                  e.preventDefault();
                }}
                onClick={e => {
                  e.stopPropagation();
                  if (open) {
                    onClose();
                  } else {
                    onOpen();
                  }
                }}
              />
            </Flex>
          )}
        </Box>
      </Box>
    </ClickAwayListener>
  );
}

// Type-safe forwardRef wrapper to preserve generic type parameter
export const SearchableCombobox = React.forwardRef(SearchableComboboxComponent) as <T>(
  props: SearchableComboboxProps<T> & { ref?: React.Ref<HTMLDivElement> },
) => React.ReactElement;
